import React, { useState } from 'react';
import { formatCurrency } from '../utils/calculations';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import SEO from '../components/SEO';

// State-wise stamp duty and registration charges
const STAMP_DUTY_RATES = {
  maharashtra: {
    name: 'Maharashtra',
    urban: { stampDuty: 6, registration: 1, metro: 6, surcharge: 1 },
    rural: { stampDuty: 4, registration: 1 },
    women: -1, // 1% concession for women
    notes: 'Mumbai/Thane: 6% + 1% surcharge, Others: 5%',
  },
  karnataka: {
    name: 'Karnataka',
    urban: { stampDuty: 5, registration: 1 },
    rural: { stampDuty: 5, registration: 1 },
    women: 0,
    notes: 'Flat 5% across Karnataka',
  },
  delhi: {
    name: 'Delhi',
    urban: { stampDuty: 6, registration: 1 },
    rural: { stampDuty: 6, registration: 1 },
    women: -1, // 1% lower for women
    notes: '5% for women, 6% for men',
  },
  tamil_nadu: {
    name: 'Tamil Nadu',
    urban: { stampDuty: 7, registration: 4 },
    rural: { stampDuty: 7, registration: 4 },
    women: 0,
    notes: '7% stamp duty + 4% registration',
  },
  telangana: {
    name: 'Telangana',
    urban: { stampDuty: 5, registration: 0.5 },
    rural: { stampDuty: 5, registration: 0.5 },
    women: 0,
    notes: '5% + 0.5% registration',
  },
  andhra_pradesh: {
    name: 'Andhra Pradesh',
    urban: { stampDuty: 5, registration: 0.5 },
    rural: { stampDuty: 5, registration: 0.5 },
    women: 0,
    notes: '5% + 0.5% registration',
  },
  uttar_pradesh: {
    name: 'Uttar Pradesh',
    urban: { stampDuty: 7, registration: 1 },
    rural: { stampDuty: 5, registration: 1 },
    women: -2, // 2% concession for women
    notes: 'Women: 5% urban, 3% rural',
  },
  gujarat: {
    name: 'Gujarat',
    urban: { stampDuty: 4.9, registration: 1 },
    rural: { stampDuty: 4.9, registration: 1 },
    women: 0,
    notes: '4.9% + 1% registration',
  },
  rajasthan: {
    name: 'Rajasthan',
    urban: { stampDuty: 6, registration: 1 },
    rural: { stampDuty: 5, registration: 1 },
    women: -1,
    notes: 'Women get 1% concession',
  },
  west_bengal: {
    name: 'West Bengal',
    urban: { stampDuty: 7, registration: 1 },
    rural: { stampDuty: 6, registration: 1 },
    women: 0,
    notes: 'Kolkata Metro: 7%, Others: 6%',
  },
  kerala: {
    name: 'Kerala',
    urban: { stampDuty: 8, registration: 2 },
    rural: { stampDuty: 8, registration: 2 },
    women: 0,
    notes: '8% + 2% registration',
  },
  madhya_pradesh: {
    name: 'Madhya Pradesh',
    urban: { stampDuty: 7.5, registration: 3 },
    rural: { stampDuty: 7.5, registration: 3 },
    women: 0,
    notes: '7.5% + 3% registration',
  },
  punjab: {
    name: 'Punjab',
    urban: { stampDuty: 7, registration: 1 },
    rural: { stampDuty: 5, registration: 1 },
    women: -1,
    notes: 'Women: 1% less stamp duty',
  },
  haryana: {
    name: 'Haryana',
    urban: { stampDuty: 7, registration: 1 },
    rural: { stampDuty: 5, registration: 1 },
    women: -1,
    notes: 'Women buyers get 1% concession',
  },
};

const StampDutyCalculator = () => {
  const [state, setState] = useState('maharashtra');
  const [propertyValue, setPropertyValue] = useState(5000000);
  const [locationType, setLocationType] = useState('urban');
  const [gender, setGender] = useState('male');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const pv = Number(propertyValue) || 0;
    const stateData = STAMP_DUTY_RATES[state];
    const rates = stateData[locationType];
    
    let stampDutyRate = rates.stampDuty;
    let registrationRate = rates.registration;
    
    // Apply women concession if applicable
    if (gender === 'female' && stateData.women !== 0) {
      stampDutyRate = Math.max(0, stampDutyRate + stateData.women);
    }
    
    // Add surcharge for metro areas if applicable (Maharashtra)
    let surcharge = 0;
    if (rates.surcharge && locationType === 'urban') {
      surcharge = (pv * rates.surcharge) / 100;
    }
    
    const stampDutyAmount = (pv * stampDutyRate) / 100;
    const registrationAmount = (pv * registrationRate) / 100;
    const totalCharges = stampDutyAmount + registrationAmount + surcharge;
    const totalCost = pv + totalCharges;

    setResult({
      stateName: stateData.name,
      propertyValue,
      stampDutyRate,
      stampDutyAmount,
      registrationRate,
      registrationAmount,
      surcharge,
      totalCharges,
      totalCost,
      notes: stateData.notes,
    });
  };

  const formatCurrencyForPDF = (amount) => 'Rs. ' + Math.round(amount).toLocaleString('en-IN');

  const downloadPDF = () => {
    if (!result) return;
    const doc = new jsPDF();
    
    doc.setFontSize(18);
    doc.text('Stamp Duty & Registration Charges', 105, 20, { align: 'center' });
    
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 105, 28, { align: 'center' });

    autoTable(doc, {
      startY: 40,
      head: [['Description', 'Value']],
      body: [
        ['State', result.stateName],
        ['Property Value', formatCurrencyForPDF(result.propertyValue)],
        ['Location', locationType === 'urban' ? 'Urban/Metro' : 'Rural'],
        ['Owner Gender', gender === 'female' ? 'Female' : 'Male'],
        ['Stamp Duty Rate', `${result.stampDutyRate}%`],
        ['Stamp Duty Amount', formatCurrencyForPDF(result.stampDutyAmount)],
        ['Registration Rate', `${result.registrationRate}%`],
        ['Registration Amount', formatCurrencyForPDF(result.registrationAmount)],
        ['Surcharge (if any)', formatCurrencyForPDF(result.surcharge)],
        ['Total Charges', formatCurrencyForPDF(result.totalCharges)],
        ['Total Property Cost', formatCurrencyForPDF(result.totalCost)],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    doc.save('Stamp_Duty_Calculation.pdf');
  };

  return (
    <div className="calculator-page">
      <SEO 
        title="Stamp Duty Calculator"
        description="Free Stamp Duty Calculator India - Calculate state-wise stamp duty and registration charges on property."
        keywords="stamp duty calculator, stamp duty calculator India, registration charges calculator, property stamp duty, state-wise stamp duty"
        canonicalPath="/stamp-duty"
      />
      <div className="calculator-header">
        <h1>🏠 Stamp Duty Calculator</h1>
        <p>Calculate state-wise stamp duty and registration charges</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          <div className="input-group">
            <label>Select State</label>
            <select value={state} onChange={(e) => setState(e.target.value)}>
              {Object.entries(STAMP_DUTY_RATES).map(([key, value]) => (
                <option key={key} value={key}>{value.name}</option>
              ))}
            </select>
          </div>

          <div className="input-group">
            <label>Property Value / Agreement Value (₹)</label>
            <input
              type="number"
              value={propertyValue === '' ? '' : propertyValue}
              onChange={(e) => setPropertyValue(e.target.value === '' ? '' : Number(e.target.value))}
            />
            <small style={{ color: '#64748b', fontSize: '12px' }}>Stamp duty is calculated on higher of agreement value or circle rate</small>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="input-group">
              <label>Location Type</label>
              <select value={locationType} onChange={(e) => setLocationType(e.target.value)}>
                <option value="urban">Urban/Metro</option>
                <option value="rural">Rural/Semi-Urban</option>
              </select>
            </div>
            <div className="input-group">
              <label>Owner Gender</label>
              <select value={gender} onChange={(e) => setGender(e.target.value)}>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
          </div>

          <button className="btn btn-primary" style={{ width: '100%', marginTop: '10px' }} onClick={calculate}>
            Calculate Charges
          </button>
        </div>

        <div className="calculator-results">
          {result ? (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)' }}>
                <h3>Total Charges</h3>
                <div className="amount">{formatCurrency(result.totalCharges)}</div>
                <p style={{ opacity: 0.9, marginTop: '8px' }}>
                  {result.stampDutyRate}% Stamp Duty + {result.registrationRate}% Registration
                </p>
              </div>

              <div className="result-details">
                <div className="result-item">
                  <span className="label">State</span>
                  <span className="value">{result.stateName}</span>
                </div>
                <div className="result-item">
                  <span className="label">Property Value</span>
                  <span className="value">{formatCurrency(result.propertyValue)}</span>
                </div>
                <div className="result-item" style={{ background: '#f5f3ff' }}>
                  <span className="label">Stamp Duty ({result.stampDutyRate}%)</span>
                  <span className="value" style={{ color: '#7c3aed' }}>{formatCurrency(result.stampDutyAmount)}</span>
                </div>
                <div className="result-item">
                  <span className="label">Registration ({result.registrationRate}%)</span>
                  <span className="value">{formatCurrency(result.registrationAmount)}</span>
                </div>
                {result.surcharge > 0 && (
                  <div className="result-item">
                    <span className="label">Surcharge</span>
                    <span className="value">{formatCurrency(result.surcharge)}</span>
                  </div>
                )}
                <div className="result-item" style={{ background: '#fef2f2' }}>
                  <span className="label">Total Charges</span>
                  <span className="value" style={{ color: '#ef4444', fontWeight: '600' }}>{formatCurrency(result.totalCharges)}</span>
                </div>
                <div className="result-item" style={{ background: '#ecfdf5' }}>
                  <span className="label">Total Property Cost</span>
                  <span className="value" style={{ color: '#10b981', fontWeight: '600' }}>{formatCurrency(result.totalCost)}</span>
                </div>
              </div>

              <div className="info-box" style={{ marginTop: '16px' }}>
                <p><strong>Note:</strong> {result.notes}</p>
              </div>

              <button className="btn btn-secondary" style={{ width: '100%', marginTop: '20px' }} onClick={downloadPDF}>
                Download PDF Report
              </button>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b' }}>
              <p>Enter property details to calculate stamp duty</p>
            </div>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>📌 Key Points</h4>
        <ul>
          <li>Stamp duty is calculated on the higher of agreement value or circle/ready reckoner rate</li>
          <li>Many states offer concessions to women buyers (1-2% lower)</li>
          <li>First-time homebuyers may get additional benefits under PMAY</li>
          <li>Rates may vary within states based on local municipal limits</li>
        </ul>
      </div>
    </div>
  );
};

export default StampDutyCalculator;
